import { useCallback, useMemo, useState } from 'react';
import { RegionFormSchema } from '../schemas/parameters.schema';
import type { ReferenceFormValue, StateReferenceDTO } from '../types/parameters.types';
import { toFieldErrors, type FieldErrors } from '../utils/formErrors';

interface UseRegionFormParams {
	references: StateReferenceDTO[];
	initialReference?: StateReferenceDTO;
	onConfirm: (form: ReferenceFormValue) => Promise<boolean>;
	onClose: () => void;
}

export const useRegionForm = ({ references, initialReference, onConfirm, onClose }: UseRegionFormParams) => {
	const [step, setStep] = useState<'selectBase' | 'editRegion'>(initialReference ? 'editRegion' : 'selectBase');
	const [searchQuery, setSearchQuery] = useState('');
	const [name, setName] = useState(initialReference?.referenceCode ?? '');
	const [stateCodes, setStateCodes] = useState<string[]>(initialReference?.stateCode ?? []);
	const [baseReferenceId, setBaseReferenceId] = useState<string | null>(null);
	const [errors, setErrors] = useState<FieldErrors>({});
	const [isSubmitting, setIsSubmitting] = useState(false);

	const unavailableStateCodes = useMemo(
		() => references.filter((item) => item._id !== initialReference?._id).flatMap((item) => item.stateCode),
		[references, initialReference?._id]
	);

	const selectBase = useCallback((reference: StateReferenceDTO) => {
		setBaseReferenceId(reference._id);
		setSearchQuery('');
		setStep('editRegion');
	}, []);

	const skipBase = useCallback(() => {
		setSearchQuery('');
		setStep('editRegion');
	}, []);

	const toggleState = useCallback((code: string) => {
		setErrors((previous) => ({ ...previous, stateCodes: '' }));
		setStateCodes((previous) =>
			previous.includes(code) ? previous.filter((item) => item !== code) : [...previous, code]
		);
	}, []);

	const changeName = (value: string) => {
		setErrors((previous) => ({ ...previous, name: '' }));
		setName(value);
	};

	const submit = async () => {
		const parsed = RegionFormSchema.safeParse({ name, stateCodes });
		if (!parsed.success) {
			setErrors(toFieldErrors(parsed.error));
			return;
		}
		setIsSubmitting(true);
		const saved = await onConfirm({ ...parsed.data, baseReferenceId });
		setIsSubmitting(false);
		if (saved) onClose();
	};

	return {
		step,
		searchQuery,
		name,
		stateCodes,
		unavailableStateCodes,
		errors,
		isSubmitting,
		changeName,
		setSearchQuery,
		selectBase,
		skipBase,
		toggleState,
		submit,
	};
};
